import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { Info } from '@phosphor-icons/react'
import ReactTooltip from 'react-tooltip'

import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import EditorText from '../../../EditorText/EditorText'
import MediaUpload from '../../../MediaUpload/MediaUpload'
import CheckboxField from '../../../FormLegacy/FormFieldsV2/CheckboxField'
import RadioField from '../../../FormLegacy/FormFieldsV2/RadioField'
import DateField from '../../../FormLegacy/FormFieldsV2/DateField'
import SelectField from '../../../FormLegacy/FormFieldsV2/SelectField'
import {
  fetchAllUnitOptions,
  getInspectionLocationInput,
  getInspectionFrequencyFields,
  MAX_STAFF_OPTIONS,
  TRIGGER_TYPES,
  TRIGGER_OPTIONS,
} from '../../helpers'
import { LOCATION_TYPES, toUnitOption } from '../../unitSelection'
import { selectStaffOptions } from '../../../../reducers/maintenanceStaffReducer'
import { selectPropertyManagerOptions } from '../../../../reducers/Organisation/propertyManagersReducer'
import API from '../../../../api/api'

const MAX_PAGE_SIZE = 50

const InspectionSettings = ({
  values,
  errors,
  building,
  staffOptions,
  propertyManagerOptions,
  isScheduledInspection,
  unitsPrefetchEnabled,
  handleChange = () => {},
  setFieldValue = () => {},
  setValues = () => {},
}) => {
  const { t } = useTranslation(['internal-inspections'])
  const [allUnitsLoaded, setAllUnitsLoaded] = useState(false)
  const [yardiOptions, setYardiOptions] = useState([])
  const [unitTypes, setUnitTypes] = useState([])
  const [unitOptions, setUnitOptions] = useState([])
  const [page, setPage] = useState(1)

  const isScheduledOrOneTime =
    isScheduledInspection || values.recurrenceType === TRIGGER_OPTIONS.ONE_TIME
  const isYardi = building && !!building.yardiPropertyId && !isScheduledInspection
  const isRentManager = building && !!building.rentManagerPropertyId && !isScheduledInspection

  const isIntegratedWith = isYardi || isRentManager

  // Paginated fetch kept only for the scheduled-inspection path, which still uses the
  // single-select SelectDropdownInput (see singleUnitOnly below).
  const fetchUnits = async (values, skip, search = '', isScrolling = false) => {
    try {
      if (!building) return
      const skip = (page - 1) * MAX_PAGE_SIZE
      const {
        data: { data = [] },
      } = await API.getUnits(
        building.id,
        isScrolling ? skip : 0,
        MAX_PAGE_SIZE,
        search,
        values.phase ? values.phase : '',
      )

      const options = data.map(({ unit }) => toUnitOption(unit))

      if (options.length < MAX_PAGE_SIZE) {
        setAllUnitsLoaded(true)
        setPage(1)
      }
      setPage((prev) => prev + 1)
      setUnitOptions(options)
    } catch (error) {
      console.error(error)
      setUnitOptions([])
    }
  }

  const fetchAllUnits = async (phase = '') => {
    if (!building) return
    setUnitOptions(await fetchAllUnitOptions(building.id, phase))
  }

  const fetchUnitTypes = async () => {
    try {
      const { data } = await API.getUnitTypes(building.id)
      if (data) {
        const options = data.map((name, index) => ({
          id: index + 1,
          text: name,
          name,
        }))
        setUnitTypes(options)
      }
    } catch ({ message }) {
      console.error(message)
    }
  }

  const fetchYardiPropertyIds = async () => {
    try {
      const { data } = await API.getYardiPropertyIds(building.id)
      if (data) {
        const options = data.map(({ yardiPropertyId }) => ({
          value: yardiPropertyId,
          label: yardiPropertyId,
        }))
        setYardiOptions(options)
      }
    } catch ({ message }) {
      console.error(message)
    }
  }

  useEffect(() => {
    if (isIntegratedWith && !isScheduledInspection) {
      if (isYardi) {
        fetchYardiPropertyIds()
      }

      fetchUnitTypes()
    }
  }, [isYardi, building])

  useEffect(() => {
    // Note: gated by the parent so a scheduled-inspection edit (which keeps the
    // paginated single-select picker) never triggers the full-list request.
    if (building && unitsPrefetchEnabled) {
      fetchAllUnits()
    }
  }, [building, unitsPrefetchEnabled])

  return (
    <React.Fragment>
      <span className="inspections-title">
        {t('internal-inspections:inspectionSettings.title')}
      </span>
      <div>
        <span className="input-label">{t('internal-inspections:templateView.inspectionName')}</span>
        <TextField
          name="name"
          onChange={handleChange}
          value={values.name}
          placeholder={t('internal-inspections:inspectionSettings.enterInspectionNamePlaceholder')}
          isError={!!errors.name}
          errorLabel={errors.name}
        />
      </div>
      <div>
        <span className="input-label">
          {t('internal-inspections:templateView.instructions')}{' '}
          <span className="input-label--light">
            {t('internal-inspections:frequencyFields.optional')}
          </span>
        </span>
        <EditorText
          name="description"
          onChange={(content, text) => setFieldValue('description', text.trim() ? content : '')}
          value={values.description}
          placeholder={t('internal-inspections:templateView.instructionsPlaceholder')}
        />
      </div>
      <div>
        <span className="input-label">
          {t('internal-inspections:inspectionSettings.uploadDocument')}{' '}
          <span className="input-label--light">
            {t('internal-inspections:frequencyFields.optional')}
          </span>
        </span>
        <MediaUpload
          files={values.attachments}
          mediaTypes={['doc', 'pdf', 'image']}
          onUpdate={(files) => setFieldValue('attachments', files)}
          multiple
        />
      </div>
      <CheckboxField
        name="noteRequired"
        data-testid="noteRequired"
        label={t('internal-inspections:templateView.requiredCompletionNote')}
        value={values.noteRequired}
        onChange={handleChange}
      />
      <CheckboxField
        name="photosRequired"
        data-testid="photosRequired"
        label={t('internal-inspections:templateView.requiredCompletionPhotos')}
        value={values.photosRequired}
        onChange={handleChange}
      />
      <CheckboxField
        name="reviewRequired"
        data-testid="reviewRequired"
        label={t('internal-inspections:createInspectionFromTemplate.requireReviewAfterCompletion')}
        value={values.reviewRequired}
        onChange={handleChange}
      />
      <div className="divider" />
      <div>
        <span className="input-label">
          {t('internal-inspections:createInspectionFromTemplate.selectLocation')}
        </span>
        <div className="radio-buttons-container">
          <div className="radio-buttons-label">
            <span className="input-label--small">
              {t('internal-inspections:createInspectionFromTemplate.single')}
            </span>
            {unitTypes.length > 0 && (
              <React.Fragment>
                <span className="input-label--small">
                  {t('internal-inspections:createInspectionFromTemplate.locationGroup')}{' '}
                  <Info data-tip data-for="location-group" />
                </span>
                <ReactTooltip className="tooltip" id="location-group" place="top" effect="solid">
                  <div>
                    {t('internal-inspections:createInspectionFromTemplate.locationGroupTooltip')}
                  </div>
                </ReactTooltip>
              </React.Fragment>
            )}
          </div>
          <div className="radio-buttons">
            <RadioField
              label={t('internal-inspections:createInspectionFromTemplate.unit')}
              name="locationType"
              value={LOCATION_TYPES.UNIT}
              checked={values.locationType === LOCATION_TYPES.UNIT}
              onChange={() =>
                setValues({
                  ...values,
                  locationType: LOCATION_TYPES.UNIT,
                  locationValue: isScheduledInspection ? '' : [],
                })
              }
            />
            <RadioField
              label={t('internal-inspections:createInspectionFromTemplate.commonArea')}
              name="locationType"
              value={LOCATION_TYPES.COMMON_AREA}
              checked={values.locationType === LOCATION_TYPES.COMMON_AREA}
              onChange={() =>
                setValues({
                  ...values,
                  locationType: LOCATION_TYPES.COMMON_AREA,
                  locationValue: '',
                })
              }
            />
            <RadioField
              label={t('internal-inspections:createInspectionFromTemplate.other')}
              name="locationType"
              value={LOCATION_TYPES.OTHER}
              checked={values.locationType === LOCATION_TYPES.OTHER}
              onChange={() =>
                setValues({
                  ...values,
                  locationType: LOCATION_TYPES.OTHER,
                  locationValue: '',
                })
              }
            />
            {unitTypes.length > 0 && (
              <RadioField
                label={t('internal-inspections:createInspectionFromTemplate.unitTypes')}
                name="locationType"
                value={LOCATION_TYPES.UNIT_TYPES}
                checked={values.locationType === LOCATION_TYPES.UNIT_TYPES}
                onChange={() =>
                  setValues({
                    ...values,
                    locationType: LOCATION_TYPES.UNIT_TYPES,
                    locationValue: [],
                  })
                }
              />
            )}
          </div>
          {isYardi && yardiOptions.length > 0 && (
            <SelectField
              name="phase"
              options={yardiOptions}
              placeholder={t(
                'internal-inspections:createInspectionFromTemplate.selectPhasePlaceholder',
              )}
              onChange={(e) => {
                handleChange(e)
                fetchAllUnits(e.target.value || '')
                if (values.locationType === LOCATION_TYPES.UNIT) {
                  setFieldValue('locationValue', [])
                }
              }}
              value={values.phase}
            />
          )}
          <div className="select-location">
            {getInspectionLocationInput(
              values,
              handleChange,
              setFieldValue,
              {
                fetchUnits,
                unitOptions,
                allUnitsLoaded,
                unitTypes,
                singleUnitOnly: isScheduledInspection,
              },
              t,
            )}
          </div>
          {!!errors.locationValue && (
            <div className="input-label--error">{errors.locationValue}</div>
          )}
        </div>
      </div>
      <div className="divider" />
      <div>
        <span className="input-label">
          {t('internal-inspections:createInspectionFromTemplate.assignTo')}{' '}
          <span className="input-label--light">
            {t('internal-inspections:frequencyFields.optional')}
          </span>
        </span>
        <Select
          placeholder={t(
            'internal-inspections:createInspectionFromTemplate.searchByStaffNamePlaceholder',
          )}
          className="search-select-container"
          classNamePrefix="select-primary"
          options={[...staffOptions, ...propertyManagerOptions]}
          isMulti
          closeMenuOnSelect={false}
          onChange={(items) =>
            setFieldValue(
              'assigneeIds',
              items.map((item) => item.value).slice(0, MAX_STAFF_OPTIONS),
            )
          }
          value={[...staffOptions, ...propertyManagerOptions].filter(
            (option) => values.assigneeIds && values.assigneeIds.includes(option.value),
          )}
          isOptionDisabled={(option) =>
            values.assigneeIds &&
            values.assigneeIds.length >= MAX_STAFF_OPTIONS &&
            !values.assigneeIds.includes(option.value)
          }
        />
        <span className="input-label--desc">
          {t('internal-inspections:createInspectionFromTemplate.selectUpTo5Assignees')}
        </span>
      </div>
      <div className="divider" />
      {isScheduledOrOneTime ? (
        <div>
          <span className="input-label">
            {t('internal-inspections:createInspectionFromTemplate.scheduleDate')}
          </span>
          <DateField
            name="date"
            placeholderText={t(
              'internal-inspections:createInspectionFromTemplate.dateFormatPlaceholder',
            )}
            autoComplete="off"
            dateFormat="MM/dd/yyyy"
            minDate={new Date()}
            value={values.date}
            onChange={handleChange}
            onChangeRaw={(e) => e.preventDefault()}
            isError={!!errors.date}
            errorLabel={errors.date}
          />
        </div>
      ) : (
        <React.Fragment>
          <div>
            <span className="input-label">
              {t('internal-inspections:createInspectionFromTemplate.setFrequency')}
            </span>
            <div className="radio-buttons-container">
              <div className="radio-buttons">
                <RadioField
                  label={t('internal-inspections:createInspectionFromTemplate.timeBased')}
                  name="frequency"
                  value={TRIGGER_TYPES.TIME}
                  checked={values.frequency === TRIGGER_TYPES.TIME}
                  onChange={(e) => {
                    setValues({
                      ...values,
                      recurrenceType: 'weekly',
                      date: null,
                      frequency: e.target.value,
                    })
                  }}
                />
                <RadioField
                  label={t('internal-inspections:createInspectionFromTemplate.triggerBased')}
                  name="frequency"
                  value={TRIGGER_TYPES.TRIGGER}
                  checked={values.frequency === TRIGGER_TYPES.TRIGGER}
                  onChange={(e) => {
                    setValues({
                      ...values,
                      recurrenceType: TRIGGER_TYPES.TRIGGER,
                      dayOfWeek: null,
                      recurrenceEndDate: null,
                      frequency: e.target.value,
                    })
                  }}
                />
              </div>
            </div>
          </div>
          {getInspectionFrequencyFields(values, handleChange, setFieldValue, errors, t)}
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

InspectionSettings.propTypes = {
  values: PropTypes.object,
  errors: PropTypes.object,
  building: PropTypes.object,
  staffOptions: PropTypes.array,
  propertyManagerOptions: PropTypes.array,
  isScheduledInspection: PropTypes.bool,
  unitsPrefetchEnabled: PropTypes.bool,
  handleChange: PropTypes.func,
  setFieldValue: PropTypes.func,
  setValues: PropTypes.func,
}

export default connect(
  (state) => ({
    staffOptions: selectStaffOptions(state),
    propertyManagerOptions: selectPropertyManagerOptions(state),
  }),
  null,
)(InspectionSettings)
